<script generic="T extends DataTableProps['value']" lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type {
  DataTableProps,
  DataTableRowClickEvent,
  DataTableRowReorderEvent,
} from 'primevue/datatable';
import DataTable from 'primevue/datatable';
import { computed, ref, useSlots, useTemplateRef } from 'vue';

import VToolTip from '@shared/component/VToolTip';
import VButton from '../VButton/VButton.vue';
import VCallToAction from '../VCallToAction/VCallToAction.vue';
import type { Filters, Property, ValueColors } from './types';
import VColumn from './VColumn.vue';
import VDataTableFilters from './VDataTableFilters.vue';
import VDataTableSearch from './VDataTableSearch.vue';

const props = withDefaults(
  defineProps<{
    alwaysShowPaginator?: DataTableProps['alwaysShowPaginator'];
    borderless?: boolean;
    currentPageReportTemplate?: DataTableProps['currentPageReportTemplate'];
    defaultEmptyDescription?: string;
    defaultEmptyTitle?: string;
    exportable?: boolean;
    filterableProperties?: Array<Property<keyof T>>;
    globalFilterFields?: DataTableProps['globalFilterFields'];
    paginator?: DataTableProps['paginator'];
    paginatorTemplate?: DataTableProps['paginatorTemplate'];
    loading?: DataTableProps['loading'];
    removableSort?: DataTableProps['removableSort'];
    rows?: DataTableProps['rows'];
    scrollable?: DataTableProps['scrollable'];
    scrollHeight?: DataTableProps['scrollHeight'];
    searchable?: boolean;
    showRowArrow?: boolean;
    showAddButton?: boolean;
    addButtonLabel?: string;
    sortField?: DataTableProps['sortField'];
    sortOrder?: DataTableProps['sortOrder'];
    reorderableColumns?: DataTableProps['reorderableColumns'];
    stateKey?: DataTableProps['stateKey'];
    stateStorage?: DataTableProps['stateStorage'];
    value: T;
    valueColors?: ValueColors<T>;
  }>(),
  {
    addButtonLabel: 'Toevoegen',
    borderless: false,
    currentPageReportTemplate: 'van {totalPages}',
    defaultEmptyTitle: 'Deze lijst is leeg',
    exportable: false,
    paginator: true,
    paginatorTemplate:
      'FirstPageLink PrevPageLink JumpToPageInput CurrentPageReport NextPageLink LastPageLink',
    removableSort: true,
    rows: 50,
    scrollable: true,
    scrollHeight: 'flex',
    showRowArrow: undefined,
  },
);

const emit = defineEmits<{
  addButtonClicked: [value?: Event];
  rowClick: [value: DataTableRowClickEvent];
  rowReorder: [value: DataTableRowReorderEvent];
}>();
const filters = defineModel<Filters<T>>('filters', { default: {} });

const expandedRows = ref([]);

const slots = useSlots();

const hasOnlySearchFilter = computed(
  () => Object.keys(filters.value).length === 1 && filters.value?.global,
);

function resetFilters() {
  filters.value = {} as Filters<T>;
}

const emptyFiltersMessage = computed(() => {
  if (hasOnlySearchFilter.value) {
    return `Niks kunnen vinden voor “${filters.value.global.value}”`;
  }

  if (Object.keys(filters.value).length) {
    return 'Niks kunnen vinden met de gekozen filters';
  }

  return props.defaultEmptyTitle;
});

const resetFilterButtonLabel = computed(() =>
  hasOnlySearchFilter.value ? 'Zoekfilter resetten' : 'Verwijder filters',
);

const dataTableRef = useTemplateRef('dataTableRef');

function exportCsv() {
  dataTableRef.value?.exportCSV();
}
</script>

<template>
  <DataTable
    ref="dataTableRef"
    v-model:expandedRows="expandedRows"
    row-group-mode="subheader"
    v-if="slots.default"
    v-bind="props"
    @row-click="(e) => emit('rowClick', e)"
    @row-reorder="(e) => emit('rowReorder', e)"
    :filters
  >
    <template v-if="props.filterableProperties || props.searchable || props.exportable" #header>
      <div class="tw-flex tw-gap-4 max-md:tw-flex-col-reverse md:tw-items-end">
        <VDataTableFilters
          v-if="props.filterableProperties"
          v-model="filters"
          :properties="props.filterableProperties"
          :values="props.value"
          :value-colors="props.valueColors"
        />

        <div class="tw-flex tw-flex-row tw-gap-4 md:tw-ml-auto">
          <slot name="search">
            <VDataTableSearch
              v-if="props.searchable"
              v-model="filters"
              class="tw-shrink-0 md:tw-w-[325px]"
            />
          </slot>
          <VToolTip label="Exporteren naar CSV" v-if="props.exportable">
            <VButton
              aria-label="Exporteren naar CSV"
              prepend-icon="far fa-file-csv"
              inverted
              @click="exportCsv()"
            />
          </VToolTip>
        </div>
      </div>
    </template>

    <slot></slot>
    <!-- Hotfix until https://github.com/primefaces/primevue/issues/5360 is solved -->
    <VColumn v-if="true" class="tw-hidden" header="" field="" />

    <template v-if="props.showRowArrow">
      <VColumn v-if="true" field="row-arrow" class="tw-w-0 tw-pr-6">
        <template #body>
          <FontAwesomeIcon class="tw-text-sm tw-text-grey-400" :icon="['far', 'chevron-right']" />
        </template>
      </VColumn>
    </template>

    <template #empty>
      <VCallToAction
        class="tw-px-4 tw-py-14"
        :description="props.defaultEmptyDescription"
        :icon="['fal', 'wind']"
        :title="emptyFiltersMessage"
        v-if="!props.loading"
      >
        <div class="tw-flex tw-flex-col tw-gap-4">
          <VButton
            v-if="Object.keys(filters).length"
            inverted
            type="primary"
            @click="resetFilters"
            data-testid="reset-filter"
          >
            {{ resetFilterButtonLabel }}
          </VButton>

          <VButton
            v-if="showAddButton"
            align="center"
            inverted
            prepend-icon="fa-solid fa-plus"
            @click="(e) => emit('addButtonClicked', e)"
          >
            {{ props.addButtonLabel }}
          </VButton>
        </div>
      </VCallToAction>
    </template>

    <template #paginatorfirstpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevrons-left']" />
    </template>

    <template #paginatorprevpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevron-left']" />
    </template>

    <template #paginatornextpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevron-right']" />
    </template>

    <template #paginatorlastpagelinkicon="slotProps">
      <FontAwesomeIcon v-bind="slotProps" :icon="['fas', 'chevrons-right']" />
    </template>

    <template #expansion="slotProps">
      <slot name="expansion" :props="slotProps"></slot>
    </template>

    <template v-if="props.showAddButton" #groupfooter>
      <VButton
        class="tw-w-full"
        align="center"
        icon-only-responsiveness
        inverted
        prepend-icon="fa-solid fa-plus"
        @click="(e) => emit('addButtonClicked', e)"
      >
        {{ props.addButtonLabel }}
      </VButton>
    </template>

    <template v-else-if="slots.groupfooter" #groupfooter="slotProps">
      <slot name="groupfooter" :props="slotProps"></slot>
    </template>
  </DataTable>
</template>
